var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { State, Action, Selector } from '@ngxs/store';
import { EngineStateModel } from './engine.state.model';
import { Load, LoadSuccess, SetReady, SetGraph, SetEngineChapter, SetEnginePosition, SetEngineChapterInit, SynchronizeVisibleNodes, ClearGraph, ResetEnginePosition, ToggleCameraBar, ToggleLabels, SetActiveTool, PlayAnimation, CenterCameraOnVisible, SetDisplay, SetVisible, SynchronizeRenderableNodes, SynchronizePickableNodes } from './engine.actions';
import { BdGraphService } from '../../../lib/graph/graph.service';
import { EngineService } from '../engine.service';
import { BdGraph } from 'src/lib/graph/graph.model';
import { EnginePositionModel } from '../models/engine-position.model';
import { EngineVisibilityEnum } from '../models/engine-visibility.enum';
import { EngineCameraState } from './camera/engine.camera.state';
import { ToolEnum } from '../models/tool.enum';
var enginePositionDefaults = new EnginePositionModel(0, 0, 0, 0, EngineVisibilityEnum.Hidden);
var EngineState = /** @class */ (function () {
    function EngineState(graphService, engineService) {
        this.graphService = graphService;
        this.engineService = engineService;
    }
    /**
     * Selectors
     */
    EngineState.getEngine = function (state) {
        return state.engine;
    };
    EngineState.getReady = function (state) {
        return state.ready;
    };
    EngineState.getDisplay = function (state) {
        return state.display;
    };
    EngineState.getVisible = function (state) {
        return state.visible;
    };
    EngineState.getViewGraph = function (state) {
        return state.viewGraph;
    };
    EngineState.getViewGraphNode = function (state) {
        return function (objectId) {
            return state.viewGraph.nodes[objectId];
        };
    };
    EngineState.getEnginePosition = function (state) {
        return state.enginePosition;
    };
    EngineState.getChapter = function (state) {
        return state.chapter;
    };
    EngineState.getActiveTool = function (state) {
        return state.activeTool;
    };
    EngineState.getShowCameraBar = function (state) {
        return state.showCameraBar;
    };
    EngineState.getShowLabels = function (state) {
        return state.showLabels;
    };
    EngineState.getChapterInit = function (state) {
        return state.chapterInit;
    };
    EngineState.getAnimationPlaying = function (state) {
        return state.animationPlaying;
    };
    /**
     * Actions
     */
    EngineState.prototype.load = function (context, action) {
        context.dispatch(new LoadSuccess('engine'));
    };
    EngineState.prototype.loadSuccess = function (_a, action) {
        var patchState = _a.patchState;
        patchState({
            engine: action.result
        });
    };
    EngineState.prototype.setReady = function (context, action) {
        context.patchState({
            ready: action.ready
        });
    };
    EngineState.prototype.setDisplay = function (context, action) {
        context.patchState({
            display: action.display
        });
    };
    EngineState.prototype.setVisible = function (context, action) {
        context.patchState({
            visible: action.visible
        });
    };
    EngineState.prototype.setEnginePosition = function (context, action) {
        context.patchState({
            enginePosition: __assign({}, action.position, { right: action.position.left + action.position.width, bottom: action.position.top + action.position.height })
        });
    };
    EngineState.prototype.resetEnginePosition = function (context, action) {
        context.patchState({
            enginePosition: enginePositionDefaults
        });
    };
    EngineState.prototype.setChapter = function (context, action) {
        context.patchState({
            chapter: action.chapterId
        });
    };
    EngineState.prototype.setChapterInit = function (context, action) {
        context.patchState({
            chapterInit: action.chapterInit
        });
    };
    EngineState.prototype.setActiveTool = function (context, action) {
        context.patchState({
            activeTool: action.tool
        });
    };
    /**
     * Graph Actions #TODO: Move into own state
     */
    EngineState.prototype.setGraph = function (context, action) {
        // Set graph state
        context.patchState({
            viewGraph: __assign({}, action.graph)
        });
    };
    EngineState.prototype.clearGraph = function (context, action) {
        context.patchState({
            viewGraph: new BdGraph()
        });
    };
    EngineState.prototype.centerCameraOnVisible = function (context, action) {
        var visibleLeaves = this.engineService.getVisibleLeaves();
        var boundary = this.engineService.getBoundaryFromObjects(visibleLeaves);
        var lookAt = this.engineService.getCameraLookAt();
        lookAt.look = {
            x: (boundary.xmin + boundary.xmax) / 2.0,
            y: (boundary.ymin + boundary.ymax) / 2.0,
            z: (boundary.zmin + boundary.zmax) / 2.0
        };
        Human.renderer.camera.fly.flyTo(lookAt);
    };
    EngineState.prototype.toggleCameraBar = function (context, action) {
        context.patchState({
            showCameraBar: action.show
        });
    };
    EngineState.prototype.toggleLabels = function (context, action) {
        context.patchState({
            showLabels: action.show
        });
    };
    EngineState.prototype.synchronizeVisibleNodes = function (context, action) {
        var _this = this;
        var viewGraph = context.getState().viewGraph;
        var leafViewNodeIds = this.stateMapToNodeIds(viewGraph, action.leafVisibilityState);
        // set unique view node leaf state by querying corresponding human object state
        var leafState = {};
        leafViewNodeIds.forEach(function (viewNodeId) {
            var leafids = _this.engineService.getLeafObjects(viewNodeId);
            var visibility = leafids.reduce(function (vis, objectId) { return vis || Human.scene.objects[objectId].shown; }, false);
            leafState[viewNodeId] = { 'shown': visibility };
        });
        this.graphService.synchronize(viewGraph, { 'shown': 'trueWins' }, leafState);
        // patch the state
        context.patchState({
            viewGraph: __assign({}, viewGraph)
        });
    };
    EngineState.prototype.synchronizeRenderableNodes = function (context, action) {
        var viewGraph = context.getState().viewGraph;
        var leafViewNodeIds = this.stateMapToNodeIds(viewGraph, action.leafRenderableState);
        // view graph leaf state
        var leafState = {};
        leafViewNodeIds.forEach(function (leafId) {
            leafState[leafId] = { 'renderable': true };
        });
        this.graphService.synchronize(viewGraph, { 'renderable': 'falseWins' }, leafState);
    };
    EngineState.prototype.synchronizePickableNodes = function (context, action) {
        var _this = this;
        var viewGraph = context.getState().viewGraph;
        var leafViewNodeIds = this.stateMapToNodeIds(viewGraph, action.leafPickableState);
        // set unique view node leaf state by querying corresponding human object state
        var leafState = {};
        leafViewNodeIds.forEach(function (viewNodeId) {
            var leafids = _this.engineService.getLeafObjects(viewNodeId);
            var pickable = leafids.reduce(function (pick, objectId) { return pick || Human.scene.objects[objectId].pickable; }, false);
            leafState[viewNodeId] = { 'pickable': pickable };
        });
        this.graphService.synchronize(viewGraph, { 'pickable': 'trueWins' }, leafState);
        // patch the state
        context.patchState({
            viewGraph: __assign({}, viewGraph)
        });
    };
    EngineState.prototype.playAnimation = function (context, action) {
        context.patchState({
            animationPlaying: action.play
        });
    };
    EngineState.prototype.stateMapToNodeIds = function (viewGraph, stateMap) {
        var _this = this;
        // map human objects to relevant view nodes (if any);
        var ids = Object.keys(stateMap)
            .map(function (objectId) { return _this.engineService.getNode(viewGraph, objectId); })
            .filter(function (node) { return !!node; })
            .map(function (node) { return node.id; });
        return new Set(ids);
    };
    __decorate([
        Action(Load),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, Load]),
        __metadata("design:returntype", void 0)
    ], EngineState.prototype, "load", null);
    __decorate([
        Action(LoadSuccess),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, LoadSuccess]),
        __metadata("design:returntype", void 0)
    ], EngineState.prototype, "loadSuccess", null);
    __decorate([
        Action(SetReady),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, SetReady]),
        __metadata("design:returntype", void 0)
    ], EngineState.prototype, "setReady", null);
    __decorate([
        Action(SetDisplay),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, SetDisplay]),
        __metadata("design:returntype", void 0)
    ], EngineState.prototype, "setDisplay", null);
    __decorate([
        Action(SetVisible),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, SetVisible]),
        __metadata("design:returntype", void 0)
    ], EngineState.prototype, "setVisible", null);
    __decorate([
        Action(SetEnginePosition),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, SetEnginePosition]),
        __metadata("design:returntype", void 0)
    ], EngineState.prototype, "setEnginePosition", null);
    __decorate([
        Action(ResetEnginePosition),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, ResetEnginePosition]),
        __metadata("design:returntype", void 0)
    ], EngineState.prototype, "resetEnginePosition", null);
    __decorate([
        Action(SetEngineChapter),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, SetEngineChapter]),
        __metadata("design:returntype", void 0)
    ], EngineState.prototype, "setChapter", null);
    __decorate([
        Action(SetEngineChapterInit),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, SetEngineChapterInit]),
        __metadata("design:returntype", void 0)
    ], EngineState.prototype, "setChapterInit", null);
    __decorate([
        Action(SetActiveTool),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, SetActiveTool]),
        __metadata("design:returntype", void 0)
    ], EngineState.prototype, "setActiveTool", null);
    __decorate([
        Action(SetGraph),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, SetGraph]),
        __metadata("design:returntype", void 0)
    ], EngineState.prototype, "setGraph", null);
    __decorate([
        Action(ClearGraph),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, SetGraph]),
        __metadata("design:returntype", void 0)
    ], EngineState.prototype, "clearGraph", null);
    __decorate([
        Action(CenterCameraOnVisible),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, CenterCameraOnVisible]),
        __metadata("design:returntype", void 0)
    ], EngineState.prototype, "centerCameraOnVisible", null);
    __decorate([
        Action(ToggleCameraBar),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, ToggleCameraBar]),
        __metadata("design:returntype", void 0)
    ], EngineState.prototype, "toggleCameraBar", null);
    __decorate([
        Action(ToggleLabels),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, ToggleLabels]),
        __metadata("design:returntype", void 0)
    ], EngineState.prototype, "toggleLabels", null);
    __decorate([
        Action(SynchronizeVisibleNodes),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, SynchronizeVisibleNodes]),
        __metadata("design:returntype", void 0)
    ], EngineState.prototype, "synchronizeVisibleNodes", null);
    __decorate([
        Action(SynchronizeRenderableNodes),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, SynchronizeRenderableNodes]),
        __metadata("design:returntype", void 0)
    ], EngineState.prototype, "synchronizeRenderableNodes", null);
    __decorate([
        Action(SynchronizePickableNodes),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, SynchronizePickableNodes]),
        __metadata("design:returntype", void 0)
    ], EngineState.prototype, "synchronizePickableNodes", null);
    __decorate([
        Action(PlayAnimation),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, PlayAnimation]),
        __metadata("design:returntype", void 0)
    ], EngineState.prototype, "playAnimation", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [EngineStateModel]),
        __metadata("design:returntype", void 0)
    ], EngineState, "getEngine", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [EngineStateModel]),
        __metadata("design:returntype", void 0)
    ], EngineState, "getReady", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [EngineStateModel]),
        __metadata("design:returntype", void 0)
    ], EngineState, "getDisplay", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [EngineStateModel]),
        __metadata("design:returntype", void 0)
    ], EngineState, "getVisible", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [EngineStateModel]),
        __metadata("design:returntype", void 0)
    ], EngineState, "getViewGraph", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [EngineStateModel]),
        __metadata("design:returntype", void 0)
    ], EngineState, "getViewGraphNode", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [EngineStateModel]),
        __metadata("design:returntype", void 0)
    ], EngineState, "getEnginePosition", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [EngineStateModel]),
        __metadata("design:returntype", void 0)
    ], EngineState, "getChapter", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [EngineStateModel]),
        __metadata("design:returntype", void 0)
    ], EngineState, "getActiveTool", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [EngineStateModel]),
        __metadata("design:returntype", void 0)
    ], EngineState, "getShowCameraBar", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [EngineStateModel]),
        __metadata("design:returntype", void 0)
    ], EngineState, "getShowLabels", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [EngineStateModel]),
        __metadata("design:returntype", void 0)
    ], EngineState, "getChapterInit", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [EngineStateModel]),
        __metadata("design:returntype", void 0)
    ], EngineState, "getAnimationPlaying", null);
    EngineState = __decorate([
        State({
            name: 'engine',
            defaults: {
                loading: false,
                ready: false,
                viewGraph: new BdGraph(),
                engine: '',
                chapter: '',
                chapterInit: {
                    id: '',
                    init: false,
                    camera: false,
                    annotations: false,
                    graph: false,
                    properties: false,
                    particles: false,
                    clips: false,
                    postprocessing: false,
                    transparency: false
                },
                showCameraBar: true,
                showLabels: true,
                activeTool: ToolEnum.Select,
                animationPlaying: true,
                display: true,
                visible: true
            },
            children: [
                EngineCameraState
            ]
        }),
        __metadata("design:paramtypes", [BdGraphService,
            EngineService])
    ], EngineState);
    return EngineState;
}());
export { EngineState };
